import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pMap from 'p-map-browser';

import PrintProductConfiguratorHeaderPane from './components/PrintProductConfiguratorHeaderPane';
import PrintProductConfiguratorControlsPane from './components/PrintProductConfiguratorControlsPane';
import PrintProductConfiguratorPreviewPane from './components/PrintProductConfiguratorPreviewPane';
import usePrintProductConfiguration from './usePrintProductConfiguration';
import PrintProductMountingOptionsAlertModal, {
  PRINT_PRODUCT_MOUNTING_OPTIONS_ALERT_MODAL,
} from './components/PrintProductMountingOptionsAlertModal/PrintProductMountingOptionsAlertModal';
import { shouldAlertPrintMountingOptions } from './components/PrintProductMountingOptionsAlertModal/helpers';
import useOpenModal from '../../../../hooks/useOpenModal';
import DesignEditorLoadingIndicator from '../../../../components/global/DesignEditor/components/DesignEditorLoadingIndicator';
import DesignEditor from '../../../../components/global/DesignEditor';
import {
  CARD_ELEMENT_TYPE,
  CARD_SIDE,
  ORIENTATION,
} from '../../../cards/constants';
import AccountCardEmptyTextAlertModal, {
  ACCOUNT_CARD_EMPTY_TEXT_ALERT_MODAL,
} from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/components/AccountCardEmptyTextAlertModal/AccountCardEmptyTextAlertModal';
import {
  shouldAlertEmptyPhotos,
  shouldAlertEmptyText,
} from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/components/AccountCardEmptyTextAlertModal/helpers';
import useOpenAlert from '../../../../hooks/useOpenAlert';
import { TEXT_PRESETS } from '../../../../components/global/CardDesigner/components/CardDesignerCardElements/CardDesignerCardElementText/helpers';
import { getAutoFitPositionPhotoCropbox } from '../../../cards/components/CardElementPhoto/helpers';
import { getTextElementPresetData } from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/helpers';
import useIdentityJerseyColors from '../../../../apps/pro/views/HierarchyItem/Matches/hooks/useIdentityJerseyColors';
import { getMaxThumb } from '../../../../util/photo';
import {
  getAccountCardElementsConfiguration,
  getCardTemplateSizing,
  isCardElementEditable,
} from '../../../cards/components/AccountCard/helpers';

const propTypes = {
  className: PropTypes.string,
  printProductId: PropTypes.string.isRequired,
  printProductSizeId: PropTypes.string,
  availablePrintProductSizeIds: PropTypes.arrayOf(PropTypes.string),
  photo: PropTypes.shape({
    id: PropTypes.string,
  }),
  orientation: PropTypes.oneOf(Object.values(ORIENTATION)),
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  defaultAccountCard: PropTypes.shape({
    id: PropTypes.string,
  }),
  buttonText: PropTypes.node,
  controlsDisabled: PropTypes.bool,
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_album: PropTypes.bool,
  }),
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  presetCardElementConfigurationInputs: PropTypes.object,
  disablePhotoWatermarkOverlay: PropTypes.bool,
  allowPhotoChange: PropTypes.bool,
  storeFetchedAccountCard: PropTypes.bool,
  printBackgrounds: PropTypes.array,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
  isAllPhotos: PropTypes.bool,
};

const PRESET_FILL_STATUS = {
  LOADING: 'LOADING',
  FILLING: 'FILLING',
  FILLED: 'FILLED',
};

const PrintProductConfigurator = ({
  className,
  printProductId,
  printProductSizeId,
  printProductSizeServiceIds,
  availablePrintProductSizeIds = [],
  photo,
  orientation,
  onClose,
  onComplete,
  defaultAccountCard,
  buttonText,
  controlsDisabled = false,
  albumMembershipGroup,
  identity,
  presetCardElementConfigurationInputs,
  disablePhotoWatermarkOverlay,
  allowPhotoChange = false,
  storeFetchedAccountCard = false,
  printBackgrounds,
  watermarkSettings,
  isAllPhotos,
}) => {
  const classes = classNames({
    [className]: !!className,
    'print-product-configurator': true,
  });

  const [presetFillStatus, setPresetFillStatus] = useState(
    PRESET_FILL_STATUS.LOADING,
  );

  const {
    globalPrintProductAlbumCustomPrint,
    printProduct,
    printProductConfiguration: {
      selectedPrintProductSize,
      handlePrintProductSizeSelect,
      selectedPrintProductMount,
      handlePrintProductMountSelect,
      selectedCardSide,
      handleCardSideSelect,
    },
    accountCardConfiguration: {
      loadingAccountCard,
      accountCard,
      onElementConfigurationChange,
      cardElementLoading,
      cardSides,
    },
  } = usePrintProductConfiguration({
    printProductId,
    defaultPrintProductSizeId: printProductSizeId,
    defaultPrintProductSizeServiceIds: printProductSizeServiceIds,
    defaultAccountCardId: defaultAccountCard?.id,
    defaultOrientation: orientation,
    presetCardElementConfigurationInputs,
    storeFetchedAccountCard,
  });
  const { teamName, levelName } = useIdentityJerseyColors({
    identityId: identity?.id,
    albumFolderId: albumMembershipGroup?.is_album
      ? albumMembershipGroup?.album?.root_album_folder?.id
      : albumMembershipGroup?.id,
  });
  const openModal = useOpenModal();
  const openAlert = useOpenAlert();

  const allowsDefaultPhoto = useMemo(() => {
    // If not from APS Lab, allow default.
    if (!(printProduct?.print_partner?.name ?? '').includes('APS')) {
      return true;
    }

    // If not a Team print, allow default.
    if (
      !(selectedPrintProductSize?.roes_info?.label ?? '')
        .toLowerCase()
        .includes('team')
    ) {
      return true;
    }

    // If the photo has > 1 face, allow default.
    if (photo?.faces?.total > 1) {
      return true;
    }

    return false;
  }, [printProduct, selectedPrintProductSize, photo]);

  const editablePhotoElements = useMemo(() => {
    const elements = getAccountCardElementsConfiguration(
      accountCard,
      cardElementLoading,
    );

    return (elements ?? []).filter(
      element =>
        element.cardElement.type === CARD_ELEMENT_TYPE.PHOTO &&
        element.side === CARD_SIDE.FRONT &&
        isCardElementEditable(element),
    );
  }, [accountCard, cardElementLoading]);

  useEffect(() => {
    const onFillEditableElements = async editableTextElements => {
      setPresetFillStatus(PRESET_FILL_STATUS.FILLING);

      if (editablePhotoElements?.length === 1 && photo && allowsDefaultPhoto) {
        const { cardElement, cardElementConfiguration } =
          editablePhotoElements[0] ?? {};

        // We don't want to replace the cropbox if there is an existing configuration.
        if (!cardElementConfiguration?.photo?.id) {
          const { width: cardWidth, height: cardHeight } =
            getCardTemplateSizing(cardSides?.[0]?.card_template);
          await onElementConfigurationChange(editablePhotoElements[0], {
            photo_id: photo.id,
            is_editable: true,
            cropbox: getAutoFitPositionPhotoCropbox({
              cardElement,
              cardWidth,
              cardHeight,
              thumb: getMaxThumb(photo?.thumbnail_urls),
              faceboxes: (photo?.faces?.records ?? []).map(
                ({ facebox }) => facebox,
              ),
            }),
          });
        }
      }

      await pMap(editableTextElements, async element => {
        const { cardElementConfiguration } = element ?? {};
        const presetKey = Object.keys(TEXT_PRESETS).find(
          k => TEXT_PRESETS[k] === cardElementConfiguration?.help_placeholder,
        );
        // We only want to automatically update values where a preset is established.
        // Therefore, we ensure a matching key exists on our array.
        if (!cardElementConfiguration?.text && presetKey) {
          const text = getTextElementPresetData({
            presetLabel: TEXT_PRESETS[presetKey],
            identity,
            albumMembershipGroup,
            teamName,
            levelName,
          });
          if (text) {
            await onElementConfigurationChange(element, { text });
          } else {
            // Pass dumb text here. The opacity will hide it, but the dumb content
            // will make the system assume that the entity is "filled" for empty text
            // slot purposes.
            await onElementConfigurationChange(element, {
              text: 'NULL',
              opacity: 0,
              is_editable: false,
            });
          }
        }
      });

      setPresetFillStatus(PRESET_FILL_STATUS.FILLED);
    };

    if (
      presetFillStatus === PRESET_FILL_STATUS.LOADING &&
      accountCard &&
      !cardElementLoading
    ) {
      const editableTextElements = getAccountCardElementsConfiguration(
        accountCard,
        cardElementLoading,
      ).filter(
        element =>
          element.cardElement.type === CARD_ELEMENT_TYPE.TEXT &&
          element.side === CARD_SIDE.FRONT &&
          isCardElementEditable(element),
      );

      if (
        editableTextElements.length > 0 ||
        (editablePhotoElements?.length === 1 && photo && allowsDefaultPhoto)
      ) {
        onFillEditableElements(editableTextElements);
      } else {
        setPresetFillStatus(PRESET_FILL_STATUS.FILLED);
      }
    }
  }, [accountCard, cardElementLoading]);

  if (!printProduct || presetFillStatus !== PRESET_FILL_STATUS.FILLED) {
    return <DesignEditorLoadingIndicator />;
  }

  const handleComplete = () => {
    onComplete({
      accountCard,
      albumCustomPrint: globalPrintProductAlbumCustomPrint,
      printProduct,
      printProductSize: selectedPrintProductSize,
    });
  };

  return (
    <DesignEditor className={classes}>
      <PrintProductConfiguratorHeaderPane
        buttonText={buttonText}
        printProduct={printProduct}
        onClose={onClose}
        onComplete={() => {
          if (shouldAlertEmptyText({ accountCard })) {
            openModal(ACCOUNT_CARD_EMPTY_TEXT_ALERT_MODAL, {
              accountCard,
              onComplete: handleComplete,
            });
          } else if (
            shouldAlertPrintMountingOptions({
              selectedPrintProductSize,
              selectedPrintProductMount,
            })
          ) {
            openModal(PRINT_PRODUCT_MOUNTING_OPTIONS_ALERT_MODAL);
          } else if (shouldAlertEmptyPhotos({ accountCard })) {
            openAlert({
              title: 'Photos missing',
              message:
                'It looks like you still need to select photos for some of the photo slots.',
            });
          } else {
            handleComplete();
          }
        }}
        completeDisabled={!accountCard}
      />

      {!controlsDisabled && (
        <PrintProductConfiguratorControlsPane
          printProduct={printProduct}
          selectedPrintProductSize={selectedPrintProductSize}
          availablePrintProductSizeIds={availablePrintProductSizeIds}
          handlePrintProductSizeSelect={handlePrintProductSizeSelect}
          selectedPrintProductMount={selectedPrintProductMount}
          handlePrintProductMountSelect={handlePrintProductMountSelect}
          cardSides={cardSides}
          selectedCardSide={selectedCardSide}
          handleCardSideSelect={handleCardSideSelect}
        />
      )}

      <PrintProductConfiguratorPreviewPane
        accountCard={accountCard}
        loading={loadingAccountCard}
        onElementConfigurationChange={async (element, config) => {
          if (config?.orientation) {
            await handlePrintProductSizeSelect?.(
              printProductSizeId,
              config.orientation,
            );
          }
          await onElementConfigurationChange(element, config);
        }}
        cardElementLoading={cardElementLoading}
        printProduct={printProduct}
        albumMembershipGroup={albumMembershipGroup}
        identity={identity}
        defaultPhoto={allowsDefaultPhoto ? photo : undefined}
        disablePhotoWatermarkOverlay={
          disablePhotoWatermarkOverlay ||
          // Becomes true when building an AMG for photos in SB albums.
          // This is tied to the AMG since the AMG is generated and passed over
          albumMembershipGroup?.is_photo_entitled
        }
        allowPhotoChange={allowPhotoChange || !allowsDefaultPhoto}
        printBackgrounds={printBackgrounds}
        watermarkSettings={watermarkSettings}
        isAllPhotos={isAllPhotos}
      />

      {selectedPrintProductSize && (
        <PrintProductMountingOptionsAlertModal
          printProduct={printProduct}
          selectedPrintProductSize={selectedPrintProductSize}
          selectedPrintProductMount={selectedPrintProductMount}
          handlePrintProductMountSelect={handlePrintProductMountSelect}
          onComplete={handleComplete}
        />
      )}
      <AccountCardEmptyTextAlertModal />
    </DesignEditor>
  );
};

PrintProductConfigurator.propTypes = propTypes;

export default PrintProductConfigurator;
